import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function CombinedGasLawCalculator() {
  const [p1, setP1] = useState("");
  const [v1, setV1] = useState("");
  const [t1, setT1] = useState("");
  const [p2, setP2] = useState("");
  const [v2, setV2] = useState("");
  const [t2, setT2] = useState("");

  const calculateMissing = () => {
    const values = [p1, v1, t1, p2, v2, t2];
    const emptyCount = values.filter(v => !v).length;

    if (emptyCount !== 1) {
      toast({
        title: "Invalid Input",
        description: "Please provide exactly 5 values to calculate the 6th.",
        variant: "destructive",
      });
      return;
    }

    const P1 = parseFloat(p1);
    const V1 = parseFloat(v1);
    const T1 = parseFloat(t1);
    const P2 = parseFloat(p2);
    const V2 = parseFloat(v2);
    const T2 = parseFloat(t2);

    // (P1*V1)/T1 = (P2*V2)/T2
    if (!p1) {
      const result = (P2 * V2 * T1) / (V1 * T2);
      setP1(result.toFixed(4));
      toast({ title: "P₁ Calculated", description: `P₁ = ${result.toFixed(4)} atm` });
    } else if (!v1) {
      const result = (P2 * V2 * T1) / (P1 * T2);
      setV1(result.toFixed(4));
      toast({ title: "V₁ Calculated", description: `V₁ = ${result.toFixed(4)} L` });
    } else if (!t1) {
      const result = (P1 * V1 * T2) / (P2 * V2);
      setT1(result.toFixed(4));
      toast({ title: "T₁ Calculated", description: `T₁ = ${result.toFixed(4)} K` });
    } else if (!p2) {
      const result = (P1 * V1 * T2) / (V2 * T1);
      setP2(result.toFixed(4));
      toast({ title: "P₂ Calculated", description: `P₂ = ${result.toFixed(4)} atm` });
    } else if (!v2) {
      const result = (P1 * V1 * T2) / (P2 * T1);
      setV2(result.toFixed(4));
      toast({ title: "V₂ Calculated", description: `V₂ = ${result.toFixed(4)} L` });
    } else if (!t2) {
      const result = (P2 * V2 * T1) / (P1 * V1);
      setT2(result.toFixed(4));
      toast({ title: "T₂ Calculated", description: `T₂ = ${result.toFixed(4)} K` });
    }
  };

  const reset = () => {
    setP1("");
    setV1("");
    setT1("");
    setP2("");
    setV2("");
    setT2("");
  };

  return (
    <PageLayout 
      calculatorName="Combined Gas Law Calculator" 
      calculatorPath="/calculator/combined-gas-law"
      category="chemistry"
      subcategory="Gases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Combined Gas Law Calculator</CardTitle>
            <CardDescription>
              (P₁V₁)/T₁ = (P₂V₂)/T₂
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p1">P₁ (atm)</Label>
                  <Input
                    id="p1"
                    type="number"
                    step="any"
                    value={p1}
                    onChange={(e) => setP1(e.target.value)}
                    placeholder="Initial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v1">V₁ (L)</Label>
                  <Input
                    id="v1"
                    type="number"
                    step="any"
                    value={v1}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="Initial"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t1">T₁ (K)</Label>
                  <Input
                    id="t1"
                    type="number"
                    step="any"
                    value={t1}
                    onChange={(e) => setT1(e.target.value)}
                    placeholder="Initial"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p2">P₂ (atm)</Label>
                  <Input
                    id="p2"
                    type="number"
                    step="any"
                    value={p2}
                    onChange={(e) => setP2(e.target.value)}
                    placeholder="Final"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v2">V₂ (L)</Label>
                  <Input
                    id="v2"
                    type="number"
                    step="any"
                    value={v2}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="Final"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t2">T₂ (K)</Label>
                  <Input
                    id="t2"
                    type="number"
                    step="any"
                    value={t2}
                    onChange={(e) => setT2(e.target.value)}
                    placeholder="Final"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculateMissing} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {(p1 || v1 || t1 || p2 || v2 || t2) && (
              <CalculatorSuperCalcCTA config={calculatorCTAs["combined-gas-law"]} trigger={p1 + v1 + t1 + p2 + v2 + t2} />
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Combined Gas Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">(P₁V₁)/T₁ = (P₂V₂)/T₂</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Combines Boyle's, Charles's, and Gay-Lussac's laws. Leave one field blank to solve for it.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
